import React, { useState, useEffect, useCallback, useRef } from 'react';
import { orderStatusLabel } from '../orderStatus';
import { api } from '../api';
import SlotChip from '../components/SlotChip';
import { formatOrderPackaging } from '../utils/packagingDisplay';
import { openOrdersStream } from '../utils/ordersStream';
import { ordersWord } from '../utils';

function PackerOrdersPage({ showToast }) {
  const [selectedDate, setSelectedDate] = useState(getTodayDate());
  const [dates, setDates] = useState([]);
  const [ordersGrouped, setOrdersGrouped] = useState({ packaging: [], courier: [], completed: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');
  const [workflowDisabled, setWorkflowDisabled] = useState(false);
  const loadOrdersRef = useRef(null);
  const selectedDateRef = useRef(selectedDate);

  useEffect(() => { generateDates(); checkWorkflow(); }, []);

  const loadOrders = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await api.getPackerOrdersByDate(selectedDate);
      setOrdersGrouped(data);
    } catch {
      showToast('Ошибка загрузки заказов', 'error');
      setOrdersGrouped({ packaging: [], courier: [], completed: [] });
    } finally { setIsLoading(false); }
  }, [selectedDate, showToast]);

  useEffect(() => { if (selectedDate) loadOrders(); }, [selectedDate, loadOrders]);

  // Сохраняем ссылки на loadOrders и selectedDate
  useEffect(() => {
    loadOrdersRef.current = loadOrders;
  }, [loadOrders]);

  useEffect(() => {
    selectedDateRef.current = selectedDate;
  }, [selectedDate]);

  // SSE: автообновление при назначении заказа или изменении статуса
  useEffect(() => {
    // Поток заказов только для сотрудников: пропуск и переподключение — в openOrdersStream
    const es = openOrdersStream();
    console.log('[PackerOrders] SSE connection opened');
    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('[PackerOrders] SSE event:', data, 'currentSelectedDate:', selectedDateRef.current);
        if ((data.type === 'new_order' || data.type === 'order_updated') && data.delivery_date === selectedDateRef.current) {
          console.log('[PackerOrders] Reloading orders after SSE event');
          setTimeout(() => {
            if (loadOrdersRef.current) {
              loadOrdersRef.current();
            }
          }, 100);
        }
      } catch (e) {
        console.error('[PackerOrders] SSE error:', e);
      }
    };
    es.onerror = (error) => {
      console.error('[PackerOrders] SSE connection error:', error);
    };
    return () => {
      console.log('[PackerOrders] SSE connection closed');
      es.close();
    };
  }, []); // Empty dependency array - connection established once

  const checkWorkflow = async () => {
    try {
      const res = await api.getPackerWorkflowEnabled();
      if (!res.enabled) {
        setWorkflowDisabled(true);
      }
    } catch (err) {
      console.error('Failed to check packer workflow', err);
    }
  };

  function getTodayDate() { return formatDateValue(new Date()); }

  function formatDateValue(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  function formatDateDisplay(dateStr) {
    const date = new Date(dateStr + 'T00:00:00');
    return `${date.getDate()} ${date.toLocaleDateString('ru-RU', { month: 'short' })}`;
  }

  function getDayName(dateStr) {
    const days = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
    return days[new Date(dateStr + 'T00:00:00').getDay()];
  }

  const getStatusLabel = orderStatusLabel; // единые подписи (src/orderStatus.js)

  function getStatusMeta(status) {
    const map = {
      new:        { cls: 'os-new',        icon: '/icons/nv-status-new.svg' },
      packaging:  { cls: 'os-packaging',  icon: '/icons/nv-package.svg' },
      ready:      { cls: 'os-ready',      icon: '/icons/nv-status-ready.svg' },
      courier:    { cls: 'os-courier',    icon: '/icons/nv-delivery.svg' },
      completed:  { cls: 'os-completed',  icon: '/icons/nv-status-completed.svg' },
    };
    return map[status] || { cls: 'os-new', icon: null };
  }

  function generateDates() {
    const today = new Date();
    const result = [];
    for (let i = 0; i < 3; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      result.push(formatDateValue(d));
    }
    setDates(result);
  }

  // Фото собранного (фото-контроль) снимает флорист на шаге «на упаковку»;
  // упаковщик только упаковывает и крепит номер — фото ему не нужно.
  const handleMarkReady = async (orderId) => {
    console.log('[PackerOrders] Marking order as ready:', orderId);
    try {
      await api.updatePackerOrder(orderId, { status: 'ready' });
      console.log('[PackerOrders] Order marked as ready successfully');
      showToast('Заказ собран');
      loadOrders();
    } catch (error) {
      console.error('[PackerOrders] Error marking order as ready:', error);
      showToast('Ошибка обновления статуса', 'error');
    }
  };

  const activeOrders = ordersGrouped.packaging || [];
  const doneOrders = [...(ordersGrouped.courier || []), ...(ordersGrouped.completed || [])].sort((a, b) => a.id - b.id);
  const displayOrders = activeTab === 'active' ? activeOrders : doneOrders;
  const todayDate = getTodayDate();
  const totalOrders = activeOrders.length + doneOrders.length;

  return (
    <div>
      {/* 3-day navigation — бренд */}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 12, padding: '10px 0', borderBottom: '1px solid #EBEDEC' }}>
        {dates.map(date => {
          const isToday = date === todayDate;
          const isSelected = date === selectedDate;
          return (
            <button key={date} onClick={() => setSelectedDate(date)} style={{
              minWidth: 90, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
              padding: '8px 14px', '--ch': '8px', '--stroke': (isSelected ? '#1C3D2E' : (isToday ? '#B0603C' : '#D8DDDA')),
              border: isSelected ? '1px solid #1C3D2E' : (isToday ? '1px solid #B0603C' : '1px solid #D8DDDA'),
              background: isSelected ? '#1C3D2E' : '#fff',
              color: isSelected ? '#fff' : '#1C3D2E',
              fontWeight: isToday || isSelected ? 600 : 500,
              cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s, --stroke 0.15s',
            }}>
              <span style={{ fontSize: 11, opacity: 0.85 }}>{getDayName(date)}</span>
              <span style={{ fontSize: 14, fontWeight: 700 }}>{formatDateDisplay(date)}</span>
            </button>
          );
        })}
      </div>

      {/* Info bar — primary green */}
      <div style={{
        background: '#1C3D2E',
        color: '#fff', padding: '10px 16px', '--ch': '8px', marginBottom: 12,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
      }}>
        <span style={{ fontSize: 13, opacity: 0.85 }}>{getDayName(selectedDate)}, {formatDateDisplay(selectedDate)}</span>
        <span style={{ fontSize: 16, fontWeight: 700 }}>
          {totalOrders} {ordersWord(totalOrders)}
        </span>
      </div>

      {/* Tabs */}
      <div className="seg staff-tabs" style={{ marginBottom: 12 }}>
        <button type="button" onClick={() => setActiveTab('active')} className={`seg-btn${activeTab === 'active' ? ' on' : ''}`}>
          На упаковке ({activeOrders.length})
        </button>
        <button type="button" onClick={() => setActiveTab('done')} className={`seg-btn${activeTab === 'done' ? ' on' : ''}`}>
          Выполненные ({doneOrders.length})
        </button>
      </div>

      {/* Orders */}
      {workflowDisabled ? (
        <div style={{ textAlign: 'center', padding: '32px 16px', background: '#F9EBCF', borderLeft: '3px solid #7E5D00', '--ch': '8px', margin: '20px 0' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#7E5D00', marginBottom: 8 }}>Режим упаковки выключен</div>
          <div style={{ fontSize: 13, color: '#5E6561' }}>Режим упаковки отключён в настройках. Включите его на горячие даты (14 февраля, 8 марта).</div>
        </div>
      ) : isLoading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : displayOrders.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#5E6561', padding: '32px 0' }}>
          {activeTab === 'active' ? 'Нет заказов на упаковку' : 'Нет выполненных заказов'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {displayOrders.map(order => {
            const meta = getStatusMeta(order.status);
            return (
            <div key={order.id} style={cardStyle}>
              {/* Шапка карточки */}
              <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 700, fontSize: 15, color: '#1C3D2E' }}>
                  #{order.order_type === 'subscription'
                    ? `${order.subscription_order_number || order.subscription_id}/${order.subscription_delivery_number}`
                    : (order.order_number || order.id)}
                  {order.source_platform === 'flowwow' && order.external_platform_order_id && <span style={{ fontWeight: 500, color: '#C2185B' }}> / {order.external_platform_order_id}</span>}
                  {order.order_type === 'subscription' && (
                    <img src="/icons/nv-stats-subscriptions.svg" alt="" style={{ width: 14, height: 14, verticalAlign: '-2px', marginLeft: 4 }} />
                  )}
                </span>
                <span className={`badge badge-${meta.cls}`}>
                  {meta.icon && <img src={meta.icon} alt="" />}
                  {getStatusLabel(order.status)}
                </span>
                <span style={{ color: '#5E6561', fontSize: 13, fontWeight: 600 }}>· <SlotChip order={order} /></span>
              </div>

              {/* Две колонки */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  {order.items?.map((item, i) => (
                    <div key={i} style={{ fontSize: 14, fontWeight: 500, padding: '2px 0', lineHeight: 1.4, color: '#1C3D2E' }}>
                      — {item.product_name} <span style={{ color: '#B0603C', fontWeight: 700 }}>×{item.quantity}</span>
                    </div>
                  ))}

                  {order.odd_quantity && (
                    <span className="badge badge-info" style={{ marginTop: 8 }}>
                      <img src="/icons/nv-odd.svg" alt="" />
                      Нечётное кол-во
                    </span>
                  )}
                </div>

                <div style={{ fontSize: 13, color: '#1C3D2E' }}>
                  {order.florist && (
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginBottom: 6, padding: '3px 8px', background: '#DFF2E8', color: '#1C3D2E', '--ch': '5px', fontWeight: 600 }}>
                      <img src="/icons/nv-florist.svg" alt="" style={{ width: 14, height: 14 }} />
                      {order.florist.name}
                    </div>
                  )}

                  <div style={{ marginTop: order.florist ? 4 : 0, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <strong style={{ color: '#5E6561', fontWeight: 600 }}>Доставка:</strong>
                    <img src={order.delivery_type === 'delivery' ? '/icons/nv-delivery.svg' : '/icons/nv-pickup.svg'} alt="" style={{ width: 14, height: 14 }} />
                    {order.delivery_type === 'delivery' ? 'Доставка' : 'Самовывоз'}
                  </div>
                  <div style={{ marginTop: 4, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <strong style={{ color: '#5E6561', fontWeight: 600 }}>Упаковка:</strong>
                    {(() => {
                      const { label, iconSrc } = formatOrderPackaging(order);
                      return iconSrc
                        ? <><img src={iconSrc} alt="" style={{ width: 14, height: 14 }} />{label}</>
                        : label;
                    })()}
                  </div>
                </div>
              </div>

              {/* Admin comment */}
              {order.admin_comment && (
                <div style={{
                  marginTop: 10, background: '#F9EBCF', borderLeft: '3px solid #7E5D00',
                  padding: '8px 12px', '--ch': '6px', fontSize: 13
                }}>
                  <strong style={{ color: '#7E5D00', fontWeight: 700 }}>Администратор:</strong>
                  <div style={{ marginTop: 3, color: '#1C3D2E', fontWeight: 500 }}>{order.admin_comment}</div>
                </div>
              )}

              {/* Greeting */}
              {order.greeting && (
                <div style={{ marginTop: 8, background: '#DFF2E8', border: '1px solid #1C3D2E', padding: '8px 12px', '--ch': '6px', '--stroke': '#1C3D2E', fontSize: 13, color: '#1C3D2E', wordBreak: 'break-word' }}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 700, marginBottom: 4 }}>
                    <img src="/icons/mail.svg" alt="" style={{ width: 14, height: 14 }} />
                    Поздравление
                  </div>
                  <div style={{ fontStyle: 'italic' }}>«{order.greeting}»</div>
                </div>
              )}

              {activeTab === 'active' && (
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button type="button" onClick={() => handleMarkReady(order.id)} className="btn btn-primary btn-md">
                    Собран
                  </button>
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const cardStyle = { background: '#fff', '--ch': '8px', '--stroke': '#EBEDEC', padding: 14, border: '1px solid #EBEDEC' };

export default PackerOrdersPage;
